// Container da listagem: filtro de status na URL, dados, white labels e o fluxo
// de ativar/desativar. Os componentes só renderizam.
//
// O white label NÃO é filtro desta tela: vem do seletor global do menu
// (useAuth.selectedWhiteLabel), que já manda `x-white-label-uuid` em toda
// chamada. Sem loja escolhida ("Todas as lojas"), a lista é a visão global.
import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import { FRONTEND_ROUTES } from "@/constants"
import { useAuth } from "@/contexts/auth"
import { useWhiteLabels } from "@/hooks/use-white-labels"
import { usePaymentConfigurations } from "@/services/payment-configurations"
import type { PaymentConfigurationListFilters } from "@/types/payment-configurations"

import type { StatusFilterValue } from "./constants"
import { feedbackVariantOf } from "./feedback"
import { useCanWritePaymentConfigurations } from "./hooks/use-can-write-payment-configurations"
import { usePaymentConfigurationToggle } from "./hooks/use-payment-configuration-toggle"

const DEFAULT_PAGE_SIZE = 10
// Teto do core (`pageSize` max 100) — URL editada à mão não estoura o 400.
const MAX_PAGE_SIZE = 100

const positiveIntOf = (
  raw: string | null,
  fallback: number,
  max = Number.MAX_SAFE_INTEGER
) => {
  const value = Number(raw)
  return Number.isInteger(value) && value >= 1
    ? Math.min(value, max)
    : fallback
}

const statusOf = (raw: string | null): StatusFilterValue =>
  raw === "true" || raw === "false" ? raw : "all"

export const paymentConfigurationDetailHref = (
  id: string
) =>
  FRONTEND_ROUTES.PAYMENT_CONFIGURATION_DETAIL.href.replace(
    ":configId",
    encodeURIComponent(id)
  )

/**
 * URL (status e página) + white label do seletor global → query do core.
 * Exportado p/ teste.
 */
export const listFiltersFromSearchParams = (
  searchParams: URLSearchParams,
  whiteLabelId = ""
): PaymentConfigurationListFilters => {
  const status = statusOf(searchParams.get("active"))
  return {
    ...(whiteLabelId ? { whiteLabelId } : {}),
    ...(status !== "all"
      ? { active: status === "true" }
      : {}),
    page: positiveIntOf(searchParams.get("page"), 1),
    pageSize: positiveIntOf(
      searchParams.get("pageSize"),
      DEFAULT_PAGE_SIZE,
      MAX_PAGE_SIZE
    ),
  }
}

export const usePaymentConfigurationsList = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { selectedWhiteLabel } = useAuth()
  const selectedWhiteLabelId =
    selectedWhiteLabel?.uuid ?? ""
  const canWrite = useCanWritePaymentConfigurations()
  const toggle = usePaymentConfigurationToggle()

  const filters = useMemo(
    () =>
      listFiltersFromSearchParams(
        searchParams,
        selectedWhiteLabelId
      ),
    [searchParams, selectedWhiteLabelId]
  )
  const status = statusOf(searchParams.get("active"))
  // Só o que esta tela controla: a loja é do menu, "limpar" não mexe nela.
  const hasActiveFilters = status !== "all"

  const {
    data,
    isLoading,
    isError,
    error,
    isPlaceholderData,
    refetch,
  } = usePaymentConfigurations(filters)
  // Sem white label (FORBIDDEN_WHITE_LABEL_SCOPE), sem permissão (outro 403)
  // ou falha transitória — só esta última tem retry. Ver ./feedback.
  const errorVariant = isError
    ? feedbackVariantOf(error)
    : null
  const { data: whiteLabels = [] } = useWhiteLabels()

  const whiteLabelNames = useMemo(
    () =>
      new Map(
        whiteLabels.map((whiteLabel) => [
          whiteLabel.uuid,
          whiteLabel.name,
        ])
      ),
    [whiteLabels]
  )

  // Sem o nome (white label fora da lista do usuário), o uuid ainda identifica.
  const whiteLabelNameOf = useCallback(
    (id: string) => whiteLabelNames.get(id) ?? id,
    [whiteLabelNames]
  )

  /**
   * Escreve um filtro na URL com `replace` (filtrar não é navegação) e volta
   * para a página 1 — a página 3 do recorte anterior pode nem existir no novo.
   */
  const writeFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set(key, value)
    } else {
      next.delete(key)
    }
    next.delete("page")
    setSearchParams(next, { replace: true })
  }

  const handleStatusChange = (value: StatusFilterValue) =>
    writeFilter("active", value === "all" ? "" : value)

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams)
    next.delete("active")
    next.delete("page")
    setSearchParams(next, { replace: true })
  }

  const pagination = data?.pagination

  return {
    items: data?.paymentConfigurations ?? [],
    pagination: pagination
      ? {
          total: pagination.totalResults,
          currentPage: pagination.currentPage,
          totalPages: pagination.totalPages,
        }
      : null,
    isLoading,
    isError,
    errorVariant,
    isStale: isPlaceholderData,
    refetch,
    canWrite,
    status,
    hasActiveFilters,
    whiteLabelNameOf,
    handleStatusChange,
    clearFilters,
    togglingId: toggle.togglingId,
    handleActivate: toggle.handleActivate,
    handleDeactivate: toggle.handleDeactivate,
    pendingDeactivate: toggle.pendingDeactivate,
    closeDeactivate: toggle.closeDeactivate,
    confirmDeactivate: toggle.confirmDeactivate,
  }
}
